import * as React from 'react';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { CommonRootState } from '@pec/aion-ui-core/combineReducers';
import { connect } from 'react-redux';
import { DrawerLinksComponent } from '../../components/Nav/DrawerLinks';
import { ErrorItem } from '../../components/Nav/ErrorItem';
import { fetchOrganizationIfNeeded } from '@pec/aion-ui-core/actions/organization';
import { fetchOrganizationLinkIfNeeded } from '@pec/aion-ui-core/actions/fetchOrganizationLink';
import { fetchSubscriptionsIfNeeded } from '@pec/aion-ui-core/actions/subscriptions';
import { fetchUserInfoIfNeeded, hasPermission } from '@pec/aion-ui-core/actions/userInfo';
import { fetchUserOrganizationsIfNeeded } from '@pec/aion-ui-core/actions/userOrganizations';
import { getOptionsIfNeeded } from '@pec/aion-ui-core/actions/options';
import { LoadingItem } from '../../components/Nav/LoadingItem';
import { Location } from 'history';
import { RootActions } from '@pec/aion-ui-core/combineActions';
import { SubscriptionStatus, SubscriptionType } from '@pec/aion-ui-core/interfaces/subscription';
import { ThunkDispatch } from 'redux-thunk';
import { UserInfoActivitiesType } from '@pec/aion-ui-core/interfaces/userInfo';

type OwnProps = {
  organizationId?: string;
  sideNav?: JSX.Element;
  navExpanded: boolean;
  location: Location;
};

const mapStateToProps = ({
  options: { dismissOrganizationLink },
  organization: { organization, ssqId, isFetching: isFetchingOrganization, fetchError: organizationError },
  organizationLink: { organizationLink, isFetching: isFetchingOrganizationLink, error: organizationLinkError },
  subscriptions: { subscriptions },
  userInfo: { userInfo, isFetching: isFetchingUserInfo, error: userInfoError },
  userOrganizations: { userOrganizations, isFetching: isFetchingUserOrganizations, error: userOrganizationsError },
  notifications
}: CommonRootState) => ({
  organization,
  promptForOrganizationLink: organizationLink === undefined && !dismissOrganizationLink,
  ssqId,
  userInfo,
  userOrganizations,
  hasVerificationSubscription: Boolean(
    subscriptions?.some(
      ({ type, status }) => type === SubscriptionType.VerificationPackage && status === SubscriptionStatus.Active
    )
  ),
  isFetching: isFetchingOrganization || isFetchingOrganizationLink || isFetchingUserInfo || isFetchingUserOrganizations,
  error: organizationError || organizationLinkError || userInfoError || userOrganizationsError,
  organizationProfileNotifications: notifications.organizationProfile
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<CommonRootState, null, RootActions>,
  { organizationId }: OwnProps
) => ({
  fetchOrganizationIfNeeded: () => organizationId && dispatch(fetchOrganizationIfNeeded(organizationId)),
  fetchOrganizationLinkIfNeeded: () => organizationId && dispatch(fetchOrganizationLinkIfNeeded(organizationId)),
  fetchSubscriptionsIfNeeded: () => organizationId && dispatch(fetchSubscriptionsIfNeeded(organizationId)),
  fetchUserInfoIfNeeded: () => dispatch(fetchUserInfoIfNeeded()),
  fetchUserOrganizationsIfNeeded: () => dispatch(fetchUserOrganizationsIfNeeded()),
  getOptionsIfNeeded: () => dispatch(getOptionsIfNeeded())
});

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & OwnProps;

class DrawerLinks extends React.Component<Props> {
  constructor(props: Props) {
    super(props);
    props.fetchOrganizationIfNeeded();
    props.fetchOrganizationLinkIfNeeded();
    props.fetchSubscriptionsIfNeeded();
    props.fetchUserInfoIfNeeded();
    props.fetchUserOrganizationsIfNeeded();
    props.getOptionsIfNeeded();
  }

  hasGlobalPermission = (action: ActivityAction, resourceName: ActivityResourceName) =>
    hasPermission(this.props.userInfo, action, resourceName);

  hasOrganizationPermission = (action: ActivityAction, resourceName: ActivityResourceName) => {
    const { userInfo, organizationId } = this.props;

    return organizationId
      ? hasPermission(userInfo, action, resourceName, [
          {
            type: UserInfoActivitiesType.Organization,
            id: organizationId
          }
        ])
      : false;
  };

  render() {
    const {
      organizationId,
      sideNav,
      organization,
      promptForOrganizationLink,
      hasVerificationSubscription,
      ssqId,
      userInfo,
      userOrganizations,
      isFetching,
      error,
      navExpanded,
      location,
      organizationProfileNotifications
    } = this.props;

    return (organization || !organizationId) && userInfo && userOrganizations && !isFetching ? (
      <DrawerLinksComponent
        navExpanded={navExpanded}
        sideNav={sideNav}
        organization={organizationId ? organization : undefined}
        promptForOrganizationLink={promptForOrganizationLink}
        ssqId={ssqId}
        userOrganizations={userOrganizations}
        hasGlobalPermission={this.hasGlobalPermission}
        hasOrganizationPermission={this.hasOrganizationPermission}
        hasVerificationSubscription={hasVerificationSubscription}
        location={location}
        organizationProfileNotifications={organizationProfileNotifications}
      />
    ) : error ? (
      <ErrorItem />
    ) : (
      <LoadingItem />
    );
  }
}

export const DrawerLinksContainer = connect(mapStateToProps, mapDispatchToProps)(DrawerLinks);
